import { useEffect, useRef, useState } from 'preact/hooks';
import { useI18n } from '../../hooks/use-i18n';
import { SUPPORTED_LOCALES, setLocalePreference } from '../../i18n';
import { currentLocale } from '../../store/widget-store';
import type { SupportedLocale } from '../../types';
import { IconGlobe } from '../web/icons';

interface LanguageMenuProps {
  /** 触发按钮的图标尺寸,默认 20(数字人头部)。 */
  iconSize?: number;
}

/**
 * 语言切换 —— IconGlobe 图标按钮 + 下拉菜单(替代原生 select),数字人/网页客服可共用。
 *
 * widget 挂在 open shadow root,document 级 mousedown 看到的 e.target 会被 retarget 成
 * shadow host,用 contains() 永远 false 会导致菜单一开就被自己关掉 —— 必须走 composedPath()
 * 拿真正穿过 shadow 边界的路径。隐藏的原生 <select> 保留给外部测试选择器。
 */
export function LanguageMenu({ iconSize = 20 }: LanguageMenuProps) {
  const { t } = useI18n();
  const [showMenu, setShowMenu] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!showMenu) return;
    const onDoc = (e: MouseEvent) => {
      const node = rootRef.current;
      if (!node) return;
      const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
      if (path.includes(node)) return;
      if (path.length === 0 && node.contains(e.target as Node)) return;
      setShowMenu(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [showMenu]);

  const pick = (v: SupportedLocale) => {
    currentLocale.value = v;
    setLocalePreference(v);
    setShowMenu(false);
  };

  return (
    <div class="dcs-lang" ref={rootRef}>
      <button
        type="button"
        class="dcs-icon-btn dcs-lang-btn"
        aria-haspopup="listbox"
        aria-expanded={showMenu}
        aria-label={t('web.selectLanguage')}
        title={t('web.selectLanguage')}
        onClick={() => setShowMenu((v) => !v)}
      >
        <IconGlobe size={iconSize} />
      </button>
      {showMenu && (
        <ul class="dcs-lang-menu" role="listbox">
          {SUPPORTED_LOCALES.map((l) => (
            <li key={l.value} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={currentLocale.value === l.value}
                class={`dcs-lang-item${currentLocale.value === l.value ? ' is-active' : ''}`}
                onClick={() => pick(l.value as SupportedLocale)}
              >
                {l.label}
              </button>
            </li>
          ))}
        </ul>
      )}
      {/* 隐藏原生 select,保留给外部测试选择器 */}
      <select
        class="dcs-lang-select dcs-lang-sr-select"
        aria-hidden="true"
        tabIndex={-1}
        value={currentLocale.value}
        onChange={(e) => {
          const v = (e.currentTarget as HTMLSelectElement).value as SupportedLocale;
          currentLocale.value = v;
          setLocalePreference(v);
        }}
      >
        {SUPPORTED_LOCALES.map((l) => (
          <option key={l.value} value={l.value}>
            {l.label}
          </option>
        ))}
      </select>
    </div>
  );
}
